import type { Site } from "@lumora/bridge";
import {
  adminEventRows,
  adminOrderRows,
  DataTable,
  LivePanel,
  useViewModel,
} from "@lumora/ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  useEdgeDeviceVm,
  usePartnerVm,
  usePlatformPaymentsVm,
  useSiteOpsVm,
  useSlipPrintVm,
} from "./platformViewModels.js";
import { SITE_PAYMENT_COLUMNS, sitePaymentRows } from "./paymentsView.js";
import { ConfirmGate } from "./ConfirmGate.js";
import { ConnectOnboardPanel } from "./ConnectOnboardPanel.js";
import { EdgeDevicesTable } from "./EdgeDevicesTable.js";
import { SlipPrintForm, SlipPrintJobsTable } from "./SlipPrintPanel.js";
import { OwnerProvisionPanel } from "./OwnerProvisionPanel.js";
import { RecordPaymentPanel } from "./RecordPaymentPanel.js";
import { showCommerceLivePanels } from "./siteManageCapabilities.js";
import { SiteTrafficPanel } from "./SiteTrafficPanel.js";
import { FounderToggle } from "./FounderToggle.js";
import { PartnerLedgerPanel } from "./PartnerLedgerPanel.js";
import { PaymentSplitSection } from "./PaymentSplitSection.js";
import { PartnerSplitPanel } from "./PartnerSplitPanel.js";
import { PlatformPageHeader } from "./PlatformPageHeader.js";
import type { SiteLoad } from "@lumora/core";

type SiteLoadState = SiteLoad;

function SiteSummaryPanel({ siteLoad, slug }: { siteLoad: SiteLoadState; slug: string }) {
  const loading = siteLoad.status === "loading";
  const site = siteLoad.status === "ready" ? siteLoad.site : null;
  const isActive = site?.sample === false;
  const statusLabel = loading
    ? "—"
    : isActive
      ? "live (active)"
      : (site?.lifecycle ?? "unknown");
  const badgeClass = !loading && isActive ? "ops-live" : "ops-badge-neutral";

  return (
    <section className="ops-panel platform-site-summary">
      <header className="ops-panel-head">
        <h2>{site?.name ?? slug}</h2>
        <span className={badgeClass}>{statusLabel}</span>
      </header>
      <dl className="platform-site-meta">
        <div>
          <dt>Slug</dt>
          <dd>
            <code>{slug}</code>
          </dd>
        </div>
        <div>
          <dt>Domain</dt>
          <dd>{loading ? "—" : site?.customDomain || "—"}</dd>
        </div>
        <div>
          <dt>Connect email</dt>
          <dd>{loading ? "—" : site?.connectAccountEmail || "—"}</dd>
        </div>
        <div>
          <dt>Lifecycle</dt>
          <dd>{loading ? "—" : (site?.lifecycle ?? "—")}</dd>
        </div>
        <div>
          <dt>SAMPLE wallpaper</dt>
          <dd>{loading ? "—" : site?.sample === false ? "Off" : "On"}</dd>
        </div>
      </dl>
    </section>
  );
}

export function SiteBackendPage() {
  const { slug = "" } = useParams();
  const opsVm = useSiteOpsVm();
  const deviceVm = useEdgeDeviceVm(slug);
  const paymentsVm = usePlatformPaymentsVm();
  const partnerVm = usePartnerVm();
  const { siteLoad, ordersEnabled, orders, events, usage, siteUsage, storageQuota } = useViewModel(opsVm);
  const { devices, sandbox: sandboxPi, provisionToken } = useViewModel(deviceVm);
  const slipVm = useSlipPrintVm(slug);
  const slip = useViewModel(slipVm);
  const { sitePayments: payments } = useViewModel(paymentsVm);
  const [partnerReady, setPartnerReady] = useState<boolean | null>(null);
  const isPartner = siteLoad.status === "ready" && siteLoad.site.kind === "partner";

  useEffect(() => {
    let active = true;
    // Public site carries ordersEnabled (Connect-ready); admin listSites does not.
    void opsVm.loadSite(slug);
    void partnerVm.partnerConnectReady(slug).then((ready) => {
      if (active) setPartnerReady(ready);
    });
    return () => {
      active = false;
    };
  }, [opsVm, partnerVm, slug]);

  const loadOrders = useCallback(async () => {
    await opsVm.loadOrders(slug);
  }, [opsVm, slug]);
  const loadDevices = useCallback(() => deviceVm.load(), [deviceVm]);
  const loadSlipJobs = useCallback(() => slipVm.load(), [slipVm]);
  const loadEvents = useCallback(async () => {
    if (siteLoad.status !== "ready") return;
    await opsVm.loadEvents(200, slug);
  }, [opsVm, slug, siteLoad]);
  const loadPayments = useCallback(() => paymentsVm.loadSite(slug), [paymentsVm, slug]);
  const loadUsage = useCallback(
    () => Promise.all([opsVm.loadUsage(slug), opsVm.loadStorageQuota(slug)]).then(() => undefined),
    [opsVm, slug],
  );

  // Peek commerce rows so we can unhide panels when history exists (#453).
  useEffect(() => {
    if (siteLoad.status !== "ready" || isPartner) return;
    void loadPayments();
    void loadOrders();
    void loadDevices();
  }, [siteLoad.status, isPartner, loadPayments, loadOrders, loadDevices]);

  const commerceLive = useMemo(
    () =>
      showCommerceLivePanels({
        ordersEnabled,
        orderCount: orders.length,
        deviceCount: devices.length,
        paymentCount: payments.length,
      }),
    [ordersEnabled, orders.length, devices.length, payments.length],
  );

  const orderRows = useMemo(() => adminOrderRows(orders, "site"), [orders]);
  const eventRows = useMemo(() => adminEventRows(events), [events]);
  const paymentRows = useMemo(() => sitePaymentRows(payments), [payments]);

  return (
    <div className="site-platform">
      <nav className="platform-breadcrumb" aria-label="Breadcrumb">
        <Link to="/platform/sites">Sites</Link>
        <span aria-hidden="true"> / </span>
        <span aria-current="page">{slug}</span>
      </nav>
      <PlatformPageHeader
        title={isPartner ? "Manage partner" : "Manage site"}
        lead={
          isPartner
            ? `Delivery partner ${slug}. Login, Stripe onboarding, and the payment ledger.`
            : `Tenant ${slug}. Owners, hosting payment, and traffic. Kitchen panels when ordering is enabled.`
        }
      />

      <SiteSummaryPanel siteLoad={siteLoad} slug={slug} />

      {siteLoad.status === "error" ? (
        <p className="ops-error">Could not load site list. Try signing in again.</p>
      ) : null}

      {siteLoad.status === "notFound" ? (
        <p className="ops-error">
          Site not found.{" "}
          <Link to="/platform/sites">Back to Sites</Link>
        </p>
      ) : null}

      {siteLoad.status !== "notFound" ? (
        <>
          <h2 className="platform-section-title">Provisioning</h2>
          <div className="platform-provision-grid">
            <OwnerProvisionPanel
              slug={slug}
              site={siteLoad.status === "ready" ? siteLoad.site : null}
            />
            {isPartner ? (
              <ConnectOnboardPanel slug={slug} />
            ) : (
              <>
                <RecordPaymentPanel slug={slug} onRecorded={() => void loadPayments()} />
                {commerceLive ? <ConnectOnboardPanel slug={slug} /> : null}
                {siteLoad.status === "ready" ? <PartnerSplitPanel slug={slug} /> : null}
              </>
            )}
          </div>

          {isPartner ? (
            <>
              <h2 className="platform-section-title">Partner ledger</h2>
              <PartnerLedgerPanel slug={slug} connectReady={partnerReady} />
            </>
          ) : null}

          {isPartner ? null : (
          <>
          {siteLoad.status === "ready" ? (
            <FounderToggle
              founder={siteLoad.site.founder === true}
              onChange={(v) => opsVm.setFounder(slug, v)}
            />
          ) : null}
          <h2 className="platform-section-title">Live data</h2>
          <div className="ops-grid">
            <LivePanel title="Traffic" load={loadUsage} intervalMs={30_000}>
              {siteLoad.status === "ready" ? (
                <SiteTrafficPanel
                  tenantId={siteLoad.site.id}
                  platform={usage}
                  siteUsage={siteUsage}
                  storageQuota={storageQuota}
                  onSaveStorageQuota={(input) => opsVm.saveStorageQuota(slug, input)}
                />
              ) : (
                <p className="muted">Loading site…</p>
              )}
            </LivePanel>

            <LivePanel title="Payments" load={loadPayments} intervalMs={10_000}>
              <DataTable
                columns={SITE_PAYMENT_COLUMNS}
                rows={paymentRows}
                empty="No payments recorded yet."
              />
              {siteLoad.status === "ready" && siteLoad.site.kind === "site" ? (
                <PaymentSplitSection slug={slug} payments={payments} />
              ) : null}
            </LivePanel>

            {commerceLive ? (
              <>
                <LivePanel title="Orders" load={loadOrders} intervalMs={3000}>
                  <DataTable
                    columns={["#", "Name", "Pickup", "Status", "Total", "When"]}
                    rows={orderRows}
                    empty="No orders for this site yet."
                  />
                </LivePanel>

                <LivePanel title="Edge devices (Pi)" load={loadDevices} intervalMs={10_000}>
                  <label>
                    <input
                      type="checkbox"
                      checked={sandboxPi}
                      onChange={(e) => deviceVm.setSandbox(e.target.checked)}
                    />
                    Sandbox printer (does not claim live kitchen tickets)
                  </label>
                  <ConfirmGate
                    idleLabel="Provision Pi token"
                    confirmLabel="Yes, provision Pi token"
                    onConfirm={() => void deviceVm.provision()}
                  />
                  {provisionToken ? (
                    <pre className="ops-token">{"Save once:\n" + provisionToken}</pre>
                  ) : null}
                  <EdgeDevicesTable
                    devices={devices}
                    onDelete={(id) => void deviceVm.remove(id)}
                  />
                </LivePanel>

                {devices.length > 0 ? (
                  <LivePanel title="QR slips (kitchen printer)" load={loadSlipJobs} intervalMs={5000}>
                    <SlipPrintForm
                      copiesText={slip.copiesText}
                      title={slip.title}
                      qrUrl={slip.qrUrl}
                      submitting={slip.submitting}
                      error={slip.error}
                      onCopies={(v) => slipVm.setCopies(v)}
                      onTitle={(v) => slipVm.setTitle(v)}
                      onQrUrl={(v) => slipVm.setQrUrl(v)}
                      onSubmit={() => void slipVm.submit()}
                    />
                    <SlipPrintJobsTable jobs={slip.jobs} onCancel={(id) => void slipVm.cancel(id)} />
                  </LivePanel>
                ) : null}
              </>
            ) : null}

            <LivePanel title="Backend events" load={loadEvents} intervalMs={4000}>
              <DataTable columns={["Type", "Message", "When"]} rows={eventRows} />
            </LivePanel>
          </div>
          </>
          )}
        </>
      ) : null}
    </div>
  );
}
